import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  XCircle,
  RotateCcw,
  Play,
  Copy,
  Check,
  AlertTriangle,
  Loader2,
  Link2,
  CheckCircle2,
  XOctagon,
  RefreshCw,
  ChevronDown,
  Package,
  Building2,
  User,
  Calendar,
  Timer,
  Zap,
  Server,
} from 'lucide-react';
import {
  useOrderWithPayloads,
  useOrder,
  useCancelOrder,
  useRecoverOrder,
  useExecuteOrderEvent,
  useVisStatus,
  useCreateOrder,
  useCascadeOrders,
} from '../../hooks/useOrders';
import { useUrnResolver } from '../../hooks/useUrnResolver';
import StatusBadge from '../../components/common/StatusBadge';
import Tabs from '../../components/common/Tabs';
import OrderBranchTimeline from '../../components/orders/OrderBranchTimeline';
import CascadeOrderDiagram from '../../components/orders/CascadeOrderDiagram';
import OrderDocuments from '../../components/orders/OrderDocuments';
import OrderNotes from '../../components/orders/OrderNotes';
import OrderActivityLog, { ActivitySummaryBadges } from '../../components/orders/OrderActivityLog';
import LosOperationsPanel from '../../components/los/LosOperationsPanel';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { formatDate, formatDuration } from '../../utils/date';

// ── Status accent colours ──

const STATUS_ACCENT: Record<string, { bg: string; border: string; glow: string }> = {
  completed: { bg: 'from-green-500/10 to-transparent', border: 'border-green-500/30', glow: 'shadow-green-500/5' },
  failed:    { bg: 'from-red-500/10 to-transparent', border: 'border-red-500/30', glow: 'shadow-red-500/5' },
  error:     { bg: 'from-red-500/10 to-transparent', border: 'border-red-500/30', glow: 'shadow-red-500/5' },
  canceled:  { bg: 'from-gray-500/10 to-transparent', border: 'border-gray-500/30', glow: 'shadow-gray-500/5' },
  processing:{ bg: 'from-amber-500/10 to-transparent', border: 'border-amber-500/30', glow: 'shadow-amber-500/5' },
  pending:   { bg: 'from-amber-500/10 to-transparent', border: 'border-amber-500/30', glow: 'shadow-amber-500/5' },
  created:   { bg: 'from-blue-500/10 to-transparent', border: 'border-blue-500/30', glow: 'shadow-blue-500/5' },
};
const DEFAULT_ACCENT = { bg: 'from-gray-500/10 to-transparent', border: 'border-gray-500/30', glow: 'shadow-gray-500/5' };

// ── JSON viewer ──

function JsonPayloadView({ data, label }: { data: unknown; label: string }) {
  const [copied, setCopied] = useState(false);
  const jsonStr = JSON.stringify(data, null, 2);
  const hasData = data !== undefined && data !== null;

  const handleCopy = () => {
    if (!hasData) return;
    navigator.clipboard.writeText(jsonStr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</span>
        {hasData && (
          <button onClick={handleCopy} className="btn-icon p-1.5" title="Copy JSON">
            {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
      <pre className="text-[13px] leading-relaxed text-gray-800 dark:text-gray-300 overflow-auto max-h-[60vh] bg-gray-100 dark:bg-gray-950/50 rounded p-4 font-mono border border-gray-200 dark:border-gray-700/50">
        {hasData ? jsonStr : `No ${label.toLowerCase()}`}
      </pre>
    </div>
  );
}

// ── Info item ──

function InfoItem({ icon: Icon, label, value }: { icon: typeof Package; label: string; value: string | undefined | null }) {
  if (!value || value === '-') return null;
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="flex items-center justify-center w-7 h-7 rounded bg-gray-100 dark:bg-gray-800/80 shrink-0">
        <Icon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider leading-none">{label}</p>
        <p className="text-[13px] text-gray-900 dark:text-gray-200 truncate mt-0.5" title={value}>{value}</p>
      </div>
    </div>
  );
}

// ── Main page ──

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: order, isLoading, error } = useOrderWithPayloads(id!);
  const cancelOrder = useCancelOrder();
  const recoverOrder = useRecoverOrder();
  const executeEvent = useExecuteOrderEvent();
  const createOrder = useCreateOrder();
  const { resolveProductUrn, resolveLenderUrn, resolveUserUrn } = useUrnResolver();

  const hasVis = !!order?.vis?.urn;
  const hasLos = order?.originator?.includes('encompass') || order?.originator?.includes('meridianlink');
  const { data: visStatus, isLoading: visLoading, dataUpdatedAt: visLastCheckedAt, refetch: refetchVis, isFetching: visRefetching } = useVisStatus(id!, hasVis, order?.status);

  // Cascade chain — fetch chain orders + parent order for completeness
  const hasCascade = !!(order?.chainId || order?.parent);
  const { data: cascadeOrders, isLoading: cascadeLoading } = useCascadeOrders(order?.chainId);
  // Also fetch the parent order if current order has one — ensures we always show the parent-child link
  const parentId = order?.parent ? (order.parent.includes(':') ? order.parent.split(':').pop()! : order.parent) : '';
  const { data: parentOrder } = useOrder(parentId);

  // Merge cascade results with current order + parent to guarantee completeness
  const mergedCascadeOrders = useMemo(() => {
    const byUrn = new Map<string, typeof order>();
    // Add chain orders
    (cascadeOrders || []).forEach((o) => { if (o?.urn) byUrn.set(o.urn, o); });
    // Ensure current order is included
    if (order?.urn) byUrn.set(order.urn, order);
    // Ensure parent is included
    if (parentOrder?.urn) byUrn.set(parentOrder.urn, parentOrder);
    return Array.from(byUrn.values()).filter((o): o is NonNullable<typeof o> => !!o);
  }, [cascadeOrders, order, parentOrder]);

  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelMessage, setCancelMessage] = useState('');
  const [reproduceDialog, setReproduceDialog] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [showLosActions, setShowLosActions] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const orderDuration = useMemo(() => {
    if (!order) return null;
    const terminalTypes = ['complete', 'completed', 'failed', 'canceled', 'declined', 'withdrawn', 'error'];
    const events = order.events || [];
    const terminalEvent = [...events]
      .reverse()
      .find((e) => terminalTypes.some((t) => e.type.toLowerCase().includes(t)));
    if (terminalEvent && order.createdAt) return formatDuration(order.createdAt, terminalEvent.date);
    if (order.createdAt && !['completed', 'canceled', 'failed', 'error', 'declined', 'withdrawn'].includes(order.status)) {
      return formatDuration(order.createdAt, new Date().toISOString()) + ' (in progress)';
    }
    return null;
  }, [order]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="text-center py-20">
        <p className="text-red-500 dark:text-red-400 mb-2">Failed to load order</p>
        <button onClick={() => navigate('/orders')} className="btn-secondary">Back to Orders</button>
      </div>
    );
  }

  const canCancel = ['created', 'processing', 'pending', 'queued'].includes(order.status);
  const canRecover = ['failed', 'error'].includes(order.status);
  const accent = STATUS_ACCENT[order.status] || DEFAULT_ACCENT;

  const handleReproduce = () => {
    if (!order.payloads?.request) return;
    createOrder.mutate(
      { product: order.product, payload: order.payloads.request },
      { onSuccess: (o) => navigate(`/orders/${o.uuid || o.urn}`) },
    );
    setReproduceDialog(false);
  };

  const documents = (() => {
    const reps = (order.payloads?.response?.reports as Array<{ documents?: Array<{ fileName: string; contentType: string }> }>) || [];
    return reps.flatMap((r) => r.documents || []);
  })();

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'activity', label: 'Activity' },
    { key: 'request', label: 'Request' },
    { key: 'response', label: 'Response' },
    { key: 'documents', label: 'Documents', count: documents.length },
    { key: 'notes', label: 'Notes' },
    { key: 'los', label: 'LOS Operations', hidden: !hasLos },
    { key: 'vis', label: 'VIS Details', hidden: !hasVis },
  ];

  return (
    <div>
      {/* ════════════════ HEADER ════════════════ */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/orders')} className="btn-icon shrink-0" title="Back to orders">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider leading-none">Order</p>
            <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate mt-0.5" title={order.urn}>
              {order.urn?.split(':').pop() || order.uuid}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setReproduceDialog(true)}
            disabled={!order.payloads?.request || createOrder.isPending}
            className="btn-primary gap-1.5 text-sm"
          >
            <RefreshCw className="h-4 w-4" />
            {createOrder.isPending ? 'Reproducing...' : 'Reproduce'}
          </button>
          {canRecover && (
            <button onClick={() => recoverOrder.mutate(order.urn)} disabled={recoverOrder.isPending} className="btn-secondary gap-1.5 text-sm">
              <RotateCcw className="h-4 w-4" /> Recover
            </button>
          )}
          {canCancel && (
            <button onClick={() => setCancelDialog(true)} className="btn-danger gap-1.5 text-sm">
              <XCircle className="h-4 w-4" /> Cancel
            </button>
          )}
          {hasLos && (
            <div className="relative">
              <button onClick={() => setShowLosActions((v) => !v)} className="btn-secondary gap-1.5 text-sm">
                <Server className="h-4 w-4" /> LOS
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showLosActions ? 'rotate-180' : ''}`} />
              </button>
              {showLosActions && (
                <div className="absolute right-0 top-full mt-1 z-20 card p-2 shadow-xl min-w-[180px]">
                  {order.originator?.includes('encompass') ? (
                    <>
                      {['transaction', 'force-status', 'documents', 'loan'].map((section) => {
                        const labels: Record<string, string> = {
                          transaction: 'View Transaction',
                          'force-status': 'Force Status',
                          documents: 'Upload Document',
                          loan: 'View Loan',
                        };
                        return (
                          <button
                            key={section}
                            onClick={() => { setActiveTab('los'); setShowLosActions(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50 rounded transition-colors"
                          >
                            <Server className="h-3.5 w-3.5 text-indigo-400" /> {labels[section]}
                          </button>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      {['loan', 'force-update', 'documents', 'deferred'].map((section) => {
                        const labels: Record<string, string> = {
                          loan: 'View Loan',
                          'force-update': 'Push Data',
                          documents: 'Upload Document',
                          deferred: 'Deferred Monitor',
                        };
                        return (
                          <button
                            key={section}
                            onClick={() => { setActiveTab('los'); setShowLosActions(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50 rounded transition-colors"
                          >
                            <Server className="h-3.5 w-3.5 text-teal-400" /> {labels[section]}
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="relative">
            <button onClick={() => setShowEvents((v) => !v)} className="btn-secondary gap-1.5 text-sm">
              <Zap className="h-4 w-4" /> Events
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showEvents ? 'rotate-180' : ''}`} />
            </button>
            {showEvents && (
              <div className="absolute right-0 top-full mt-1 z-20 card p-2 shadow-xl min-w-[160px]">
                {['process', 'complete', 'fail', 'pending', 'cancel'].map((evt) => (
                  <button
                    key={evt}
                    onClick={() => { executeEvent.mutate({ id: order.uuid, event: evt }); setShowEvents(false); }}
                    disabled={executeEvent.isPending}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50 rounded transition-colors"
                  >
                    <Play className="h-3.5 w-3.5 text-gray-500" /> {evt}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ════════════════ SINGLE CARD WITH TABS ════════════════ */}
      <div className="card shadow-lg overflow-hidden">
        {/* Tabs bar */}
        <div className="px-5 pt-3">
          <Tabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab}>
            {/* ──── OVERVIEW TAB ──── */}
            {activeTab === 'overview' && (
              <div className="space-y-6 -mx-5">
                {/* Status banner */}
                <div className={`bg-gradient-to-r ${accent.bg} px-5 py-4 border-b ${accent.border}`}>
                  <div className="flex items-center gap-4 flex-wrap">
                    <StatusBadge status={order.status} className="text-sm px-3 py-1" />
                    {orderDuration && (
                      <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                        <Timer className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">{orderDuration}</span>
                      </div>
                    )}
                    {order.vis && (
                      <span className="badge bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300 text-xs">VIS: {order.vis.vendor}</span>
                    )}
                  </div>
                </div>

                {/* Key info */}
                <div className="px-5">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-3">
                    <InfoItem icon={Package} label="Product" value={resolveProductUrn(order.product)} />
                    <InfoItem icon={Building2} label="Lender" value={resolveLenderUrn(order.originator)} />
                    <InfoItem icon={User} label="Requester" value={resolveUserUrn(order.requester)} />
                    <InfoItem icon={Calendar} label="Created" value={formatDate(order.createdAt)} />
                    <InfoItem icon={Calendar} label="Updated" value={formatDate(order.modificationDate)} />
                  </div>

                  {/* Activity Summary */}
                  <div className="mt-4">
                    <ActivitySummaryBadges orderUrn={order.urn} />
                  </div>

                  {/* Order Logic */}
                  {(order.chainId || order.parent || order.createdByRule) && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Link2 className="h-3.5 w-3.5 text-purple-400" />
                        <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wider">Order Logic</span>
                        {order.parent && <span className="badge bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 text-[10px]">cascade</span>}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {order.createdByRule && (
                          <div>
                            <p className="text-[10px] text-gray-500 uppercase">Rule</p>
                            <p className="text-xs text-gray-700 dark:text-gray-300 font-medium">{order.createdByRule}</p>
                          </div>
                        )}
                        {order.chainId && (
                          <div>
                            <p className="text-[10px] text-gray-500 uppercase">Chain ID</p>
                            <p className="text-xs text-gray-700 dark:text-gray-300 font-mono truncate" title={order.chainId}>{order.chainId}</p>
                          </div>
                        )}
                        {order.parent && (
                          <div>
                            <p className="text-[10px] text-gray-500 uppercase">Parent</p>
                            <p className="text-xs text-blue-400 cursor-pointer hover:underline truncate" onClick={() => navigate(`/orders/${order.parent}`)} title={order.parent}>{order.parent}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-[10px] text-gray-500 uppercase">Cascade</p>
                          <p className="text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1">
                            {order.ruleEvaluationEnabled
                              ? <><CheckCircle2 className="h-3 w-3 text-green-400" /> Enabled</>
                              : <><XOctagon className="h-3 w-3 text-gray-500" /> Disabled</>}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Order Journey */}
                <div className="px-5 pt-2">
                  <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-4">Order Journey</h3>
                  <OrderBranchTimeline
                    events={order.events || []}
                    visStatus={hasVis ? visStatus : undefined}
                    visLoading={visLoading}
                    hasVisIntegration={hasVis}
                    visLastCheckedAt={visLastCheckedAt}
                    visRefetching={visRefetching}
                    onRefreshVis={() => refetchVis()}
                  />
                </div>

                {/* VIS errors (if any, show inline on overview) */}
                {hasVis && visStatus?.context?.errors && visStatus.context.errors.length > 0 && (
                  <div className="px-5 pb-1">
                    <h3 className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-2">VIS Errors</h3>
                    <div className="space-y-1.5">
                      {visStatus.context.errors.map((err, i) => (
                        <div key={i} className="p-3 rounded bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-500/20">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-3.5 w-3.5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs font-medium text-red-700 dark:text-red-300">{err.title} {err.code && <span className="text-red-500">({err.code})</span>}</p>
                              <p className="text-xs text-red-500 dark:text-red-400/80 mt-0.5">{err.detail}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cascade Chain Diagram — show when there are multiple orders in the cascade */}
                {hasCascade && (cascadeLoading || mergedCascadeOrders.length > 1) && (
                  <div className="px-5 pt-2 pb-1">
                    <CascadeOrderDiagram
                      orders={mergedCascadeOrders}
                      currentOrderUrn={order.urn}
                      isLoading={cascadeLoading}
                      resolveProductUrn={resolveProductUrn}
                    />
                  </div>
                )}
              </div>
            )}

            {/* ──── ACTIVITY TAB ──── */}
            {activeTab === 'activity' && (
              <OrderActivityLog orderUrn={order.urn} />
            )}

            {/* ──── REQUEST TAB ──── */}
            {activeTab === 'request' && (
              <JsonPayloadView data={order.payloads?.request} label="Request Payload" />
            )}

            {/* ──── RESPONSE TAB ──── */}
            {activeTab === 'response' && (
              <JsonPayloadView data={order.payloads?.response} label="Response Payload" />
            )}

            {/* ──── DOCUMENTS TAB ──── */}
            {activeTab === 'documents' && (
              <OrderDocuments orderId={order.uuid} response={order.payloads?.response ? { payload: order.payloads.response } : undefined} />
            )}

            {/* ──── NOTES TAB ──── */}
            {activeTab === 'notes' && (
              <OrderNotes orderUrn={order.urn} />
            )}

            {/* ──── LOS OPERATIONS TAB ──── */}
            {activeTab === 'los' && hasLos && (
              <LosOperationsPanel order={order} />
            )}

            {/* ──── VIS DETAILS TAB ──── */}
            {activeTab === 'vis' && hasVis && (
              <VisDetailsTab visStatus={visStatus} visLoading={visLoading} />
            )}
          </Tabs>
        </div>

        {/* Bottom padding for the card */}
        <div className="h-5" />
      </div>

      {/* ════════════════ DIALOGS ════════════════ */}
      <ConfirmDialog
        open={cancelDialog}
        title="Cancel Order"
        message="Are you sure you want to cancel this order? This action cannot be undone."
        confirmLabel="Cancel Order"
        variant="danger"
        onConfirm={() => {
          cancelOrder.mutate({ urn: order.urn, message: cancelMessage || undefined });
          setCancelDialog(false);
          setCancelMessage('');
        }}
        onCancel={() => { setCancelDialog(false); setCancelMessage(''); }}
      />
      <ConfirmDialog
        open={reproduceDialog}
        title="Reproduce Order"
        message="This will create a new order with the same product and payload. Continue?"
        confirmLabel="Reproduce"
        onConfirm={handleReproduce}
        onCancel={() => setReproduceDialog(false)}
      />
    </div>
  );
}

// ── VIS details tab ──

function VisDetailsTab({ visStatus, visLoading }: { visStatus?: { status: string | null; vendor?: string; type?: string; message?: string; context?: { source?: string; errors?: { code: string; title: string; detail: string; meta?: Record<string, unknown> }[] } }; visLoading: boolean }) {
  if (visLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading VIS details...
      </div>
    );
  }

  if (!visStatus?.status) {
    return <p className="text-sm text-gray-500">{visStatus?.message || 'No VIS status available'}</p>;
  }

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <dt className="text-xs font-medium text-gray-500 uppercase">Status</dt>
          <dd className="mt-1"><StatusBadge status={visStatus.status.toLowerCase()} /></dd>
        </div>
        {visStatus.vendor && (
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase">Vendor</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">{visStatus.vendor}</dd>
          </div>
        )}
        {visStatus.type && (
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase">Type</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">{visStatus.type}</dd>
          </div>
        )}
        {visStatus.message && (
          <div className="sm:col-span-2 lg:col-span-3">
            <dt className="text-xs font-medium text-gray-500 uppercase">Message</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">{visStatus.message}</dd>
          </div>
        )}
      </dl>
      {visStatus.context?.errors && visStatus.context.errors.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-red-400 uppercase tracking-wider">Errors</p>
          {visStatus.context.errors.map((err, i) => (
            <div key={i} className="p-3 rounded bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-500/20">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">{err.title} {err.code && <span className="text-red-500">({err.code})</span>}</p>
                  <p className="text-sm text-red-500 dark:text-red-400/80 mt-0.5">{err.detail}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
